import Image from 'next/image';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { FooterAccordion } from './footer-accordion';
import { FooterColumn } from './footer-column';
import { FooterComplianceLinks } from './footer-compliance-links';
import { FooterContacts } from './footer-contacts';
import {
  topLinks,
  findCareLinks,
  patientResourcesLinks,
  researchAndEducationLinks,
  connectLinks,
  aboutLinks,
  accordionData,
  patientCareInfo,
  albertEinsteinMedicine,
  complianceLinks,
} from './footer-data';
import { FooterTopLinks } from './footer-top-links';

import '../montefiore.css';

export const MontefioreFooter: React.FC = () => {
  const footerHeadingId = useId();
  const { key } = useOrgContext();
  const { t } = useTranslation(key);

  return (
    <footer
      className={'mf-footer border-t bg-white'}
      aria-labelledby={footerHeadingId}
    >
      <h2 id={footerHeadingId} className={'sr-only'}>
        {t('footer.title')}
      </h2>
      <div className={'mf-wrap'}>
        <div className={'grid-cols-24 grid'}>
          <div className={'lg:col-span-18 col-span-full'}>
            <div className={'md:hidden flex justify-center py-8'}>
              <Image
                src={
                  'https://montefioreeinstein.org/modules/montefiore_einstein/storybook/static/images/logos/montefiore-einstein-primary.svg'
                }
                alt={'Montefiore Einstein logo'}
                className={'mf-logo block w-[12.5rem]'}
                width={200}
                height={40}
                priority={true}
              />
            </div>
            <FooterTopLinks links={topLinks} />
            <div
              className={'mf-footer__main-links'}
              style={{ '--column-count': '5' } as React.CSSProperties}
            >
              <FooterColumn
                title={t('montefiore.findCare')}
                links={findCareLinks}
              />
              <FooterColumn
                title={t('montefiore.patientResources')}
                links={patientResourcesLinks}
              />
              <FooterColumn
                title={t('montefiore.researchAndEducation')}
                links={researchAndEducationLinks}
              />
              <FooterColumn
                title={t('montefiore.connect')}
                links={connectLinks}
              />
              <FooterColumn title={t('montefiore.about')} links={aboutLinks} />
            </div>
            <div className={'md:hidden col-span-full border-t'}>
              <FooterAccordion accordionData={accordionData} />
            </div>
          </div>
          <div className={'mf-footer__right-col'}>
            <div className={'md:block lg:pr-0 hidden w-full pr-12'}>
              <Image
                src={
                  'https://montefioreeinstein.org/modules/montefiore_einstein/storybook/static/images/logos/montefiore-einstein-primary.svg'
                }
                alt={'Montefiore Einstein logo'}
                className={'mf-logo block w-[12.5rem]'}
                data-analytics-link-type={'logo'}
                width={200}
                height={40}
                priority={true}
                quality={100}
              />
            </div>
            <div
              className={
                'md:space-y-0 lg:space-y-32 md:grid md:grid-cols-24 lg:block lg:w-full lg:mt-60 mt-10 w-full justify-start space-y-32'
              }
            >
              <FooterContacts contactsData={patientCareInfo} />
              <hr className={'mf-divider bg-soft-gray md:hidden lg:block'} />
              <FooterContacts contactsData={albertEinsteinMedicine} />
            </div>
          </div>
        </div>
        <div className={'mf-footer__bottom'}>
          <FooterComplianceLinks complianceLinks={complianceLinks} />
        </div>
      </div>
    </footer>
  );
};
